import React, { useState } from "react";
import Button from "../../../components/ui/button";
import { displayErrorMsg } from "../../../utils/displayErrorMsg";
import colors from "../../../utils/colors";
import Input from "../../../components/input";
import { IBecomeAVerifiedDoc, TKey } from "../../../types";
import Dropdown from "../../../components/ui/dropdown";
import { useVerifyLicense } from "../../../apis/doctor";
import AuthenticationModal from "../AuthenticationModal";
import { hourGlass, warningIcon } from "../../../assets";
import { specialties } from "../../patients/utils";
import { useNavigate } from "react-router-dom";
import { TDoctorInfo } from "../../../apis/types";

interface IProps {
  doctor: TDoctorInfo | undefined; // Allow doctor to be undefined
}
const BeAVerifiedDocForm = ({ doctor }: IProps) => {
  const [details, setDetails] = useState<IBecomeAVerifiedDoc>(
    {} as IBecomeAVerifiedDoc
  );
  const [submitting, setSubmitting] = useState(false);
  const { mutate, isPending } = useVerifyLicense();
  const [open, setOpen] = useState(false);
  const [openSkip, setOpenSkip] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("access_token");

  // Handle case where doctor might be undefined
  if (!doctor) {
    // Optionally, redirect or show a more specific loading/error state
    return (
      <p>
        Loading doctor information or information is missing. Please try going
        back.
      </p>
    );
  }

  const updateState = <K extends keyof IBecomeAVerifiedDoc>(
    name: K,
    value: IBecomeAVerifiedDoc[K]
  ) => {
    setDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isDisabled =
    !details?.licenseNo || !details?.specialty || !details?.yoe;
  const labelStyles =
    "!font-lato !font-[400] !text-[14px] md:!text-[18px] !leading-[21.6px]";
  const inputStyles = "!p-[14px] md:!p-[20px] !rounded-[8px] !text-[14px]";
  return (
    <div className="w-full flex gap-[24px] flex-col">
      <div>
        <Input
          handleChange={(e) => updateState("licenseNo", e)}
          property="licenseNo"
          submitting={false}
          value={details.licenseNo}
          labelStyles={labelStyles}
          inputStyles={inputStyles}
          title="Practicing license number"
          placeholder="Enter your practicing license number"
        />
        {displayErrorMsg(
          "Practicing license number can not be empty",
          details.licenseNo,
          submitting
        )}
      </div>
      {/* <div>
        <label className={labelStyles}>Upload License (Optional)</label>
        <input
          type="file"
          accept="image/*,.pdf"
          onChange={(e) => updateState("licenseFile", e.target.files?.[0])}
          className="block w-full text-sm text-black-500 file:mr-4 file:py-2 file:px-4
      file:rounded-full file:border-0 file:text-sm file:font-semibold
      file:bg-primary file:text-white hover:file:bg-primary-dark"
        />
      </div> */}

      <div className="flex flex-col gap-2">
        <label className={`${labelStyles} text-[16px] text-black`}>
          Upload License (Optional)
        </label>
        <input
          type="file"
          accept="image/*,.pdf"
          onChange={(e) => updateState("licenseFile", e.target.files?.[0])}
          className="block w-full text-sm text-gray-700
      file:mr-4 file:py-2 file:px-4
      file:rounded-md file:border-0
      file:bg-blue-600 file:text-white
      file:font-semibold hover:file:bg-blue-700
      cursor-pointer"
        />
      </div>

      <div>
        <Input
          handleChange={(e) => updateState("yoe", e)}
          property="yoe"
          submitting={false}
          labelStyles={labelStyles}
          inputStyles={inputStyles}
          value={details.yoe}
          title="Years of Experience"
          placeholder="Enter your number of years"
        />
        {displayErrorMsg("Password can not be empty", details.yoe, submitting)}
      </div>

      <div className="flex-1">
        <p className={`text-sm mb-4 ${labelStyles}`}>Specialty</p>
        <Dropdown
          onSelect={(s) => updateState("specialty", s)}
          options={specialties}
          className="my-[-9px] w-full"
          placeholder="Which specialist are you if any?"
          inputStyles={inputStyles}
          isMulti={true}
          max="3"
        />
        {displayErrorMsg(
          `Specialty cannot be empty`,
          details.specialty,
          submitting
        )}
      </div>

      <Button
        theme="primary"
        text="Verify License"
        className="w-full rounded-md"
        loading={isPending}
        onClick={async () => {
          setSubmitting(true);

          if (!isDisabled && doctor?._id) {
            // Ensure doctor and doctor._id exist
            mutate(
              {
                doctorId: doctor._id, // Safe access
                licenseNumber: details?.licenseNo,
                specialty: details?.specialty,
                yoe: Number(details?.yoe),
              },
              {
                onSuccess: async () => {
                  // Upload license if a file was selected
                  if (details.licenseFile) {
                    const formData = new FormData();
                    formData.append("file", details.licenseFile);
                    formData.append("tag", "license");

                    try {
                      const res = await fetch(
                        `${process.env.REACT_APP_BASE_URL}/doctor/${doctor._id}/media?tag=license`, // Safe access
                        {
                          method: "POST",
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                          body: formData,
                        }
                      );

                      if (!res.ok) {
                        console.error("License upload failed");
                      }
                    } catch (err) {
                      console.error("Error uploading license:", err);
                    }
                  }

                  sessionStorage.clear();
                  setOpen(true);
                },
              }
            );
          }
        }}
      />
      <Button
        theme="tertiary-primary"
        text="Not Now"
        className="w-full rounded-md"
        onClick={() => setOpenSkip(true)}
      />
      <AuthenticationModal
        btnText="Go to login"
        headerText="Your credentials are under review"
        image={hourGlass}
        isOpen={open}
        onClose={setOpen}
        subText={`You'll be notified once it's complete`}
        title=""
        onclickProceed={() => navigate(token ? "/" : "/login")}
      />

      <AuthenticationModal
        btnText="Go to login"
        headerText="Are you sure you want to skip this step"
        image={warningIcon}
        isOpen={openSkip}
        onClose={setOpenSkip}
        subText={`If you skip this step, you won't have access to all our features`}
        title=""
        warning
        doubleBtns
        rightBtn={
          <Button
            text="Skip"
            theme="danger"
            className="!rounded-[5px] md:!rounded-[10px] !py-[5px] md:!py-[10px] !px-[20px] !flex-1"
            onClick={() => navigate(token ? "/" : "/login")}
          />
        }
        leftBtn={
          <Button
            text="Cancel"
            theme="tertiary"
            className="!rounded-[5px] md:!rounded-[10px] !py-[5px] md:!py-[10px] !px-[20px] !flex-1"
            onClick={() => setOpenSkip(false)}
          />
        }
      />
    </div>
  );
};

export default BeAVerifiedDocForm;
